import { api } from '../../store/api';

export interface MissingSheetIssue {
    type: 'missing_sheet';
    sheet: string;
}

export interface MissingColumnsIssue {
    type: 'missing_columns';
    sheet: string;
    /** 不足している必須カラム名のリスト */
    columns: string[];
    /** ファイル内の実際のヘッダー行 */
    actual_headers: string[];
}

export type ValidationIssue = MissingSheetIssue | MissingColumnsIssue;

export interface FileUploadApiError {
    error_code: string;
    message: string;
    http_status: number;
    details?: ValidationIssue[];
}

/** 非同期アップロード時のレスポンス（relay_enabled=true） */
export interface FileUploadAcceptedResponse {
    status: 'accepted';
    message: string;
    calculation_request_id: number;
    request_id: number;
    /** アップロード時刻（ISO文字列）。ダウンロードファイル名のタイムスタンプに使用 */
    uploaded_at: string | null;
}

/** ポーリング用レスポンス */
export interface CalculationStatusResponse {
    calculation_request_id: number;
    status: 'pending' | 'completed' | 'error';
    error_message: string | null;
    /** アップロード時刻（ISO文字列）。ダウンロードファイル名のタイムスタンプに使用 */
    uploaded_at: string | null;
}

/** アップロード履歴アイテム */
export interface CalculationHistoryItem {
    id: number;
    /** 0=処理中, 1=完了, 2=エラー */
    status: number;
    uploaded_filename: string;
    error_message: string | null;
    /** 出力ファイルが存在するか */
    has_result_file: boolean;
    /** operation_logs.timestamp（ISO文字列） */
    uploaded_at: string | null;
    /** 操作ユーザー名 */
    account_name: string | null;
}

/** 処理中リクエスト確認レスポンス */
export type PendingCalculationResponse =
    | { has_pending: false }
    | { has_pending: true; calculation_request_id: number | null; uploaded_filename: string; uploaded_at: string | null };

const fileUploadApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /** ファイルアップロード（非同期: JSONレスポンス / 同期: Blobレスポンス） */
    uploadFile: builder.mutation<FileUploadAcceptedResponse | Blob, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/file-upload',
          method: 'POST',
          body: formData,
          responseHandler: async (response) => {
            if (!response.ok) {
              const text = await response.text();
              try {
                return JSON.parse(text);
              } catch {
                return { message: text };
              }
            }
            const contentType = response.headers.get('content-type') ?? '';
            // 非同期モード: JSONが返る
            if (contentType.includes('application/json')) {
              return response.json();
            }
            // 同期モード（relay_enabled=false）: Excelが返る
            return response.blob();
          },
        };
      },
    }),

    /** 処理中の計算リクエストがあるか確認（ページ表示時に呼び出し） */
    getPendingCalculation: builder.query<PendingCalculationResponse, void>({
      query: () => ({
        url: '/calculation-pending',
        method: 'GET',
      }),
    }),

    /** ポーリング: 計算リクエストの状態を確認 */
    getCalculationStatus: builder.query<CalculationStatusResponse, number>({
      query: (calculationRequestId) => ({
        url: `/calculation-status/${calculationRequestId}`,
        method: 'GET',
      }),
    }),

    /** 計算結果Excelのダウンロード */
    downloadCalculationResult: builder.query<Blob, number>({
      query: (calculationRequestId) => ({
        url: `/calculation-result/${calculationRequestId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** アップロードされた元Excelのダウンロード */
    downloadUploadedFile: builder.query<Blob, number>({
      query: (calculationRequestId) => ({
        url: `/calculation-uploaded/${calculationRequestId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** アップロード履歴の取得 */
    getCalculationHistory: builder.query<CalculationHistoryItem[], void>({
      query: () => '/calculation-history',
    }),
  }),
});

export const {
  useUploadFileMutation,
  useGetPendingCalculationQuery,
  useLazyGetCalculationStatusQuery,
  useLazyDownloadCalculationResultQuery,
  useLazyDownloadUploadedFileQuery,
  useGetCalculationHistoryQuery,
} = fileUploadApi;
